import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { CustomSelect } from '../components/CustomSelect';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';
import styles from './Settings.module.css';

interface SettingsState {
  openai: {
    apiKey: string;
    model: string;
    maxTokens: number;
    temperature: number;
  };
  mle: {
    keyId: string;
    publicKey: string;
    privateKey: string;
    algorithm: string;
  };
  docker: {
    enabled: boolean;
    image: string;
    timeout: number;
  };
  scanning: {
    maxThreads: number;
    requestTimeout: number;
    retryAttempts: number;
    rateLimitDelay: number;
  };
  prompts: {
    testStrategy: string;
    pytestGeneration: string;
    codeReview: string;
  };
}

interface TestStatus {
  openai: string;
  mle: string;
  docker: string;
}

interface SettingsProps {
  onOpenRightDrawer?: (content: { title: string; component: React.ReactNode }) => void;
}

export const Settings: React.FC<SettingsProps> = ({ onOpenRightDrawer: _onOpenRightDrawer }) => {
  const { isSuperAdmin } = useAuth();
  const [settings, setSettings] = useState<SettingsState>({
    openai: {
      apiKey: '',
      model: 'gpt-4o-mini',
      maxTokens: 4000,
      temperature: 0.7,
    },
    mle: {
      keyId: '',
      publicKey: '',
      privateKey: '',
      algorithm: 'RSA-OAEP-256',
    },
    docker: {
      enabled: true,
      image: 'python:3.11-slim',
      timeout: 300000,
    },
    scanning: {
      maxThreads: 10,
      requestTimeout: 30000,
      retryAttempts: 3,
      rateLimitDelay: 1000,
    },
    prompts: {
      testStrategy: `Generate comprehensive test strategies covering OWASP API Top 10, MLE-specific tests, business logic flaws, authentication bypass, and rate limiting issues.`,
      pytestGeneration: `Generate production-ready pytest code with proper imports, configuration, test functions, MLE helpers, and vulnerability detection logic.`,
      codeReview: `Review and improve pytest code for syntax errors, security best practices, code quality, missing dependencies, and test coverage.`,
    },
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [testStatus, setTestStatus] = useState<TestStatus>({
    openai: 'disconnected',
    mle: 'disconnected',
    docker: 'disconnected',
  });
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [modelsLoading, setModelsLoading] = useState<boolean>(false);

  const loadSettings = useCallback(async () => {
    try {
      logger.info('Loading settings');
      const response = await fetch('/api/settings');

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          logger.info('Settings loaded successfully');
          setSettings((prev) => ({ ...prev, ...data.settings }));
        }
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      logger.error('Failed to load settings', { error: error.message });
      toast.error('Failed to load settings');
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const loadWallarmAIModels = async (useCurrentKey: boolean = false) => {
    const apiKey = useCurrentKey ? settings.openai.apiKey : null;

    setModelsLoading(true);
    try {
      logger.info('Loading Wallarm.AI models');

      const requestBody = useCurrentKey ? { apiKey } : {};
      const response = await fetch('/api/openai/models', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          logger.info('Wallarm.AI models loaded successfully', { count: data.models.length });
          setAvailableModels(data.models);
        } else {
          throw new Error(data.error || 'Failed to load models');
        }
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      logger.error('Failed to load Wallarm.AI models', { error: error.message });
      toast.error(`Failed to load Wallarm.AI models: ${error.message}`);
      // Clear models on error - user needs to add API key first
      setAvailableModels([]);
    } finally {
      setModelsLoading(false);
    }
  };

  const saveSettings = async () => {
    setIsLoading(true);
    try {
      logger.info('Saving settings');

      const response = await fetch('/api/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ settings }),
      });

      const result = await response.json();

      if (result.success) {
        logger.info('Settings saved successfully');
        toast.success('Settings saved successfully');
      } else {
        throw new Error(result.error || 'Failed to save settings');
      }
    } catch (error) {
      logger.error('Settings save error', { error: error.message });
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setIsLoading(false);
    }
  };

  const saveApiKey = async () => {
    if (!settings.openai.apiKey) {
      toast.error('Please enter an API key first');
      return;
    }

    setIsLoading(true);
    try {
      logger.info('Saving Wallarm.AI API key');

      const response = await fetch('/api/settings/openai-key', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ apiKey: settings.openai.apiKey }),
      });

      const result = await response.json();

      if (result.success) {
        logger.info('Wallarm.AI API key saved successfully');
        toast.success('API key saved successfully');
        // After saving the key, load models
        await loadWallarmAIModels();
      } else {
        throw new Error(result.error || 'Failed to save API key');
      }
    } catch (error) {
      logger.error('API key save error', { error: error.message });
      toast.error(error.message || 'Failed to save API key');
    } finally {
      setIsLoading(false);
    }
  };

  const testConnection = async (type: keyof TestStatus) => {
    setTestStatus((prev) => ({ ...prev, [type]: 'testing' }));

    try {
      logger.info(`Testing ${type} connection`);

      let endpoint: string | undefined, config: any;

      if (type === 'openai') {
        endpoint = '/api/test-openai';
        config = settings.openai;
      } else if (type === 'mle') {
        endpoint = '/api/test-mle';
        config = settings.mle;
      } else if (type === 'docker') {
        endpoint = '/api/test-docker';
        config = settings.docker;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ config }),
      });

      const result = await response.json();

      if (result.success) {
        logger.info(`${type} connection successful`, { response: result.response });
        setTestStatus((prev) => ({ ...prev, [type]: 'connected' }));
        toast.success(`${type === 'openai' ? 'Wallarm.AI' : type.toUpperCase()} connection successful`);

        // If Wallarm.AI connection is successful, load models using current key
        if (type === 'openai') {
          await loadWallarmAIModels(true);
        }
      } else {
        throw new Error(result.error || 'Connection failed');
      }
    } catch (error) {
      logger.error(`${type} test error`, { error: error.message });
      setTestStatus((prev) => ({ ...prev, [type]: 'error' }));
      toast.error(`${type === 'openai' ? 'Wallarm.AI' : type.toUpperCase()} connection failed: ${error.message}`);
    }
  };

  const handleInputChange = (section: keyof SettingsState, field: string, value: any) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  return (
    <div className={styles.container}>
      <div className={styles.grid}>
        {/* Wallarm.AI Configuration - Only visible to admins */}
        {isSuperAdmin() && (
          <Card className={styles.settingCard}>
            <div className={styles.settingHeader}>
              <h3 className={styles.settingTitle}>Wallarm.AI Integration</h3>
              <p className={styles.settingDescription}>
                Configure Wallarm.AI API for AI-powered business logic test generation
              </p>
            </div>
            <div className={styles.settingContent}>
              <div className={styles.formGroup}>
                <label className={styles.label}>API Key</label>
                <input
                  className={styles.input}
                  type="password"
                  value={settings.openai.apiKey}
                  onChange={(e) => handleInputChange('openai', 'apiKey', e.target.value)}
                  placeholder="sk-..."
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Model</label>
                <div className={styles.modelRow}>
                  <div className={styles.modelSelectWrapper}>
                    <CustomSelect
                      value={settings.openai.model}
                      onChange={(e) => handleInputChange('openai', 'model', e.target.value)}
                      options={
                        availableModels.length > 0
                          ? availableModels.map((model) => ({
                              value: model.id,
                              label: model.name || model.id,
                            }))
                          : [{ value: '', label: 'No models available - Please add API key and refresh' }]
                      }
                      placeholder="Select a model..."
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => loadWallarmAIModels(false)}
                    disabled={modelsLoading}
                    className={styles.refreshButton}
                  >
                    {modelsLoading ? <LoadingSpinner size="small" /> : '🔄'}
                  </Button>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Max Tokens</label>
                <input
                  className={styles.input}
                  type="number"
                  value={settings.openai.maxTokens}
                  onChange={(e) => handleInputChange('openai', 'maxTokens', parseInt(e.target.value, 10))}
                  min="100"
                  max="8000"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Temperature</label>
                <input
                  className={styles.input}
                  type="number"
                  step="0.1"
                  min="0"
                  max="2"
                  value={settings.openai.temperature}
                  onChange={(e) => handleInputChange('openai', 'temperature', parseFloat(e.target.value))}
                />
              </div>

              <div className={styles.statusIndicator} data-status={testStatus.openai}>
                <div className={styles.statusDot} data-pulse={testStatus.openai === 'testing' ? 'true' : undefined} />
                {testStatus.openai === 'connected' && 'Connected'}
                {testStatus.openai === 'error' && 'Connection Error'}
                {testStatus.openai === 'testing' && 'Testing...'}
                {testStatus.openai === 'disconnected' && 'Not Tested'}
              </div>

              <div className={styles.buttonGroup}>
                <Button variant="outline" onClick={saveApiKey} disabled={!settings.openai.apiKey || isLoading}>
                  {isLoading ? <LoadingSpinner size="small" /> : 'Save'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => testConnection('openai')}
                  disabled={!settings.openai.apiKey || testStatus.openai === 'testing'}
                >
                  {testStatus.openai === 'testing' ? <LoadingSpinner size="small" /> : 'Test Connection'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* MLE Configuration */}
        <Card className={styles.settingCard}>
          <div className={styles.settingHeader}>
            <h3 className={styles.settingTitle}>MLE Configuration</h3>
            <p className={styles.settingDescription}>Configure Message Level Encryption keys and settings</p>
          </div>
          <div className={styles.settingContent}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Key ID</label>
              <input
                className={styles.input}
                type="text"
                value={settings.mle.keyId}
                onChange={(e) => handleInputChange('mle', 'keyId', e.target.value)}
                placeholder="visa-key-001"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Public Key (JWK)</label>
              <textarea
                className={styles.textarea}
                value={settings.mle.publicKey}
                onChange={(e) => handleInputChange('mle', 'publicKey', e.target.value)}
                placeholder='{"kty":"RSA","use":"enc","key_ops":["encrypt"],...}'
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Private Key (JWK)</label>
              <textarea
                className={styles.textarea}
                value={settings.mle.privateKey}
                onChange={(e) => handleInputChange('mle', 'privateKey', e.target.value)}
                placeholder='{"kty":"RSA","use":"enc","key_ops":["decrypt"],...}'
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Algorithm</label>
              <CustomSelect
                value={settings.mle.algorithm}
                onChange={(e) => handleInputChange('mle', 'algorithm', e.target.value)}
                options={[
                  { value: 'RSA-OAEP-256', label: 'RSA-OAEP-256' },
                  { value: 'RSA-OAEP', label: 'RSA-OAEP' },
                  { value: 'A256GCM', label: 'A256GCM' },
                ]}
              />
            </div>

            <div className={styles.statusIndicator} data-status={testStatus.mle}>
              <div className={styles.statusDot} data-pulse={testStatus.mle === 'testing' ? 'true' : undefined} />
              {testStatus.mle === 'connected' && 'Valid Configuration'}
              {testStatus.mle === 'error' && 'Invalid Configuration'}
              {testStatus.mle === 'testing' && 'Testing...'}
              {testStatus.mle === 'disconnected' && 'Not Tested'}
            </div>

            <div className={styles.buttonGroup}>
              <Button
                variant="outline"
                onClick={() => testConnection('mle')}
                disabled={!settings.mle.keyId || !settings.mle.publicKey || testStatus.mle === 'testing'}
              >
                {testStatus.mle === 'testing' ? <LoadingSpinner size="small" /> : 'Test Configuration'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Docker Configuration */}
        <Card className={styles.settingCard}>
          <div className={styles.settingHeader}>
            <h3 className={styles.settingTitle}>Docker Configuration</h3>
            <p className={styles.settingDescription}>Configure Docker settings for secure test execution environment</p>
          </div>
          <div className={styles.settingContent}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Docker Image</label>
              <input
                className={styles.input}
                type="text"
                value={settings.docker.image}
                onChange={(e) => handleInputChange('docker', 'image', e.target.value)}
                placeholder="python:3.11-slim"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Execution Timeout (ms)</label>
              <input
                className={styles.input}
                type="number"
                value={settings.docker.timeout}
                onChange={(e) => handleInputChange('docker', 'timeout', parseInt(e.target.value, 10))}
                min="30000"
                max="900000"
                step="30000"
              />
            </div>

            <div className={styles.statusIndicator} data-status={testStatus.docker}>
              <div className={styles.statusDot} data-pulse={testStatus.docker === 'testing' ? 'true' : undefined} />
              {testStatus.docker === 'connected' && 'Docker Available'}
              {testStatus.docker === 'error' && 'Docker Unavailable'}
              {testStatus.docker === 'testing' && 'Testing...'}
              {testStatus.docker === 'disconnected' && 'Not Tested'}
            </div>

            <div className={styles.buttonGroup}>
              <Button
                variant="outline"
                onClick={() => testConnection('docker')}
                disabled={testStatus.docker === 'testing'}
              >
                {testStatus.docker === 'testing' ? <LoadingSpinner size="small" /> : 'Test Docker'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Scanning Configuration */}
        <Card className={styles.settingCard}>
          <div className={styles.settingHeader}>
            <h3 className={styles.settingTitle}>Scanning Configuration</h3>
            <p className={styles.settingDescription}>Configure scanning performance and behavior settings</p>
          </div>
          <div className={styles.settingContent}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Max Concurrent Threads</label>
              <input
                className={styles.input}
                type="number"
                value={settings.scanning.maxThreads}
                onChange={(e) => handleInputChange('scanning', 'maxThreads', parseInt(e.target.value, 10))}
                min="1"
                max="50"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Request Timeout (ms)</label>
              <input
                className={styles.input}
                type="number"
                value={settings.scanning.requestTimeout}
                onChange={(e) => handleInputChange('scanning', 'requestTimeout', parseInt(e.target.value, 10))}
                min="5000"
                max="120000"
                step="5000"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Retry Attempts</label>
              <input
                className={styles.input}
                type="number"
                value={settings.scanning.retryAttempts}
                onChange={(e) => handleInputChange('scanning', 'retryAttempts', parseInt(e.target.value, 10))}
                min="0"
                max="10"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Rate Limit Delay (ms)</label>
              <input
                className={styles.input}
                type="number"
                value={settings.scanning.rateLimitDelay}
                onChange={(e) => handleInputChange('scanning', 'rateLimitDelay', parseInt(e.target.value, 10))}
                min="0"
                max="5000"
                step="100"
              />
            </div>
          </div>
        </Card>

        {/* AI Prompts Configuration */}
        <Card className={styles.settingCard}>
          <div className={styles.settingHeader}>
            <h3 className={styles.settingTitle}>AI Prompts Configuration</h3>
            <p className={styles.settingDescription}>
              Customize AI prompts for test strategy generation, pytest code generation, and code review
            </p>
          </div>
          <div className={styles.settingContent}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Test Strategy Generation Prompt</label>
              <textarea
                className={styles.textarea}
                value={settings.prompts.testStrategy}
                onChange={(e) => handleInputChange('prompts', 'testStrategy', e.target.value)}
                placeholder="Prompt for AI to generate test strategies..."
                rows={4}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Pytest Code Generation Prompt</label>
              <textarea
                className={styles.textarea}
                value={settings.prompts.pytestGeneration}
                onChange={(e) => handleInputChange('prompts', 'pytestGeneration', e.target.value)}
                placeholder="Prompt for AI to generate pytest code..."
                rows={4}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Code Review Prompt</label>
              <textarea
                className={styles.textarea}
                value={settings.prompts.codeReview}
                onChange={(e) => handleInputChange('prompts', 'codeReview', e.target.value)}
                placeholder="Prompt for AI to review and improve code..."
                rows={4}
              />
            </div>
          </div>
        </Card>
      </div>

      <Card>
        <Card.Body>
          <div className={styles.buttonGroup}>
            <Button variant="outline" onClick={loadSettings} disabled={isLoading}>
              Reset
            </Button>
            <Button variant="primary" onClick={saveSettings} disabled={isLoading}>
              {isLoading ? <LoadingSpinner size="small" /> : 'Save Settings'}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};
